import mustache from "mustache";
import { BaseRequest } from "../../../../base/request/BaseRequest";
import { FormField } from "../../../base/form/FormField";
import { IInputNumberField } from "../../../definitions/form/inputs/IInputNumberField";
import { NumberConfig } from "../../../definitions/form/inputs/config/NumberConfig";
import { NumberValidation } from "../../../definitions/form/inputs/validation/NumberValidation";
import template from "./template.html";

mustache.parse(template);

/** Campo numérico (`<input type="number">`) del sistema nuevo. Enlaza un `number`
 *  del request; el input vacío escribe `0` (y un `0` se muestra vacío). */
export class InputNumberField<TRequest extends BaseRequest>
  extends FormField<NumberConfig<TRequest>>
  implements IInputNumberField
{
  private readonly _request: TRequest;
  private readonly _validation: NumberValidation;

  constructor(
    request: TRequest,
    config: NumberConfig<TRequest>,
    validation: NumberValidation,
  ) {
    super(config);
    this._request = request;
    this._validation = validation;
  }

  private _onValueChange?: () => void;

  setOnChange(handler: () => void): void {
    this._onValueChange = handler;
  }

  /** Valor numérico actual (desde el request). */
  getValue(): number {
    return this._config.get(this._request);
  }

  getTemplate(): string {
    return template;
  }

  getData(): unknown {
    const isRequired = this._validation.isRequired ?? false;
    const current = this._config.get(this._request);
    return {
      label: `${this._config.label}${isRequired ? " *" : ""}`,
      description: this._config.description ?? "",
      placeholder: this._config.placeholder ?? "",
      value: current ? String(current) : "", // 0 → vacío
      id: this._formFieldId,
    };
  }

  connect(): void {
    this._input = document.getElementById(
      `input-${this._formFieldId}`,
    ) as HTMLInputElement;
    this._error = document.getElementById(`error-${this._formFieldId}`);
    this._input?.addEventListener("input", this._onInput);
  }

  doDispose(): void {
    this._input?.removeEventListener("input", this._onInput);
  }

  getError(): string | null {
    const raw = this._input?.value.trim() ?? "";
    const { isRequired, min, max, custom } = this._validation;
    if (isRequired && raw === "") return `Ingrese ${this._config.label}`;
    const value = raw === "" ? 0 : Number(raw);
    if (!this._config.decimal && !Number.isInteger(value))
      return "Debe ser un número entero";
    if (min != null && value < min) return `Mínimo ${min}`;
    if (max != null && value > max) return `Máximo ${max}`;
    if (custom) return custom(value);
    return null;
  }

  showError(): void {
    const error = this.getError();
    if (this._error) this._error.innerHTML = error ?? "";
    this._input?.classList.toggle("error", error !== null);
  }

  focus(): void {
    this._input?.focus();
  }

  disabled(): void {
    if (this._input) this._input.disabled = true;
  }

  enabled(): void {
    if (this._input) this._input.disabled = false;
  }

  private _input: HTMLInputElement | null = null;
  private _error: HTMLElement | null = null;

  private _onInput = (): void => {
    if (!this._input) return;
    const raw = this._input.value;
    const parsed = this._config.decimal ? parseFloat(raw) : parseInt(raw);
    this._config.set(this._request, isNaN(parsed) ? 0 : parsed);
    this.showError();
    this._onValueChange?.();
  };
}
